import { merge } from 'lodash';
import React, { createContext, useReducer } from 'react';
import { riskProfiles } from '../../config/constants';
import { DeepPartial } from '../../utils/typings';

/** All amounts are in cents. All dates are in number of years */
export const pensionPeriodicDepositIntervals = ['yearly', 'monthly'] as const;

export type NibudCarNewOrSecondHand = 'Nieuw' | 'Tweedehands';
export type NibudHouseCategory = 'Koopwoning' | 'Huurwoning';
export type NibudHouseType = 'Appartement' | 'Tussenwoning' | 'Hoekwoning' | 'Vrijstaand';

export type EmploymentStatus = 'employee' | 'self-employed' | 'unemployed';
export type MaritalStatus = 'single' | 'married';
export type AccountType = 'investment' | 'saving';
export type recurringPaymentInterval = 'month' | 'year';

export type OtherIncome = 'gift' | 'heritage' | 'surplusHouse' | 'other';

export type NibudCategories = {
    house: number;
    energyAndLocalTaxes: number;
    transportation: number;
    otherUtilities: number;
    unexpected: number;
    leisure: number;
    household: number;
    other: number;
};
export type Account = {
    type: AccountType;
    currentAmount?: number;
    predictedAmount?: number;
    predictedAmountMonthly?: number;
    availableAt?: string;
    riskProfile: string;
    recurringPaymentAmount?: number;
    recurringPaymentInterval: recurringPaymentInterval;
};

export type Car = {
    type?: string;
    carBoughtStatus?: NibudCarNewOrSecondHand;
    carValue?: number;
    distance?: string;
};

export type PensionParameters = {
    general: {
        aowBase?: number;
        taxPercentage?: number; // TODO: not the way to compute net from gross pension, use scales and divide aow + pension into scales with its own percentages
    };
    personalInfo: {
        self?: {
            age?: number;
            salary?: number;
            employmentStatus?: EmploymentStatus;
            pensionAge?: number;
        };
        maritalStatus?: MaritalStatus;
        isCalculatedTogether?: boolean;
        partner?: {
            age?: number;
            salary?: number;
            employmentStatus?: EmploymentStatus;
            pensionAge?: number;
        };
    };
    expensesInfo?: {
        isKnown?: boolean;
        goal?: number;
        showModal?: boolean;
        modalStep?: number;
        nibud?: {
            house?: {
                paymentCategory?: NibudHouseCategory;
                isMortgage?: boolean; // can be derived from paymentCategory, is not changed at the moment
                houseType?: NibudHouseType;
                rent?: number;
                mortgage?: number;
                woz?: string;
            };
            carInfo?: {
                hasCar: boolean;
                cars: Car[];
            };
            calculatedExpenses?: NibudCategories;
        };
    };
    pensionInfo?: {
        modal?: {
            show: boolean;
        };
        self?: {
            isEmployedAbroad: boolean | null;
            yearsEmployedAbroad: number;
            aowGrossYearly: {
                single: number;
                together: number;
            };
            employeePensionGrossYearly?: number;
        };
        partner?: {
            isEmployedAbroad: boolean | null;
            yearsEmployedAbroad: number;
            aowGrossYearly: number;
            employeePensionGrossYearly?: number;
        };
    };
    supplementInfo?: {
        self?: {
            accountsAvailable?: boolean;
            accounts: Account[];
            additionalInvestmentAccount?: {
                isUsed: boolean,
                account: Account
            };
            additionalSavingAccount?: {
                isUsed: boolean,
                account: Account
            };
        }
        partner?: {
            accountsAvailable?: boolean;
            accounts: Account[];
        }

        isAccountAvailable?: boolean;

        yearSpaceParams: {
            factorA: number;
            FORIncrease: number;
            FORDecrease: number;
            grossYearlyIncome: number;
        }
        yearSpace?: number
        yearSpacePensionAccount?: {
            typeOfAdditionalPensionAccount: AccountType;
            riskProfileId: string;
            monthlyPaymentAmount?: number;
            predictedAmount?: number;
        }
        resultsAccount: Account | null;
        
        otherIncomeAvailable: boolean;
        otherIncome: {
            gift?: number;
            heritage?: number;
            surplusHouse?: number;
            other?: number;
        }
    };
};

type PensionParametersReducerAction = { type: 'MERGE'; payload: DeepPartial<PensionParameters> };

const pensionParametersReducer: React.Reducer<
    PensionParameters,
    { type: 'MERGE'; payload: DeepPartial<PensionParameters> }
> = (currentParameters: PensionParameters, action: PensionParametersReducerAction) => {
    switch (action.type) {
        case 'MERGE':
            return merge({}, currentParameters, action.payload);
    }
};

export const PensionContext = createContext<{
    parameters: PensionParameters;
    actions: {
        setParameters: (payload: DeepPartial<PensionParameters>) => void;
        propagate: () => void;
    };
}>({
    parameters: { personalInfo: {}, general: {} },
    actions: {
        setParameters: undefined!,
        propagate: undefined!,
    },
});

export const WithPensionContext: React.FC<{}> = ({ children }) => {
    const [pensionParameters, setPensionParameters] = useReducer(pensionParametersReducer, {
        personalInfo: {
            self: { employmentStatus: 'employee', pensionAge: 67 },
            partner: { employmentStatus: 'employee', pensionAge: 67 },
            maritalStatus: 'single',
        },
        expensesInfo: {
            isKnown: true,
            modalStep: 1,
            nibud: {
                carInfo: {
                    hasCar: true,
                    cars: [{ 
                        type: undefined,
                        distance: undefined,
                        carBoughtStatus: undefined,
                        carValue: 0
                    }]
                },
                house: { 
                    paymentCategory: undefined,
                    isMortgage: undefined },
            },
        },
        supplementInfo: {
            self: {
                accountsAvailable: false,
                accounts: [{ type: 'saving', recurringPaymentInterval: 'month', riskProfile: 'neutraal' }], 
                additionalInvestmentAccount: undefined,
                additionalSavingAccount: undefined
            },
            partner: {
                accountsAvailable: false,
                accounts: [{ type: 'saving', recurringPaymentInterval: 'month', riskProfile: 'neutraal' }]
            },
            resultsAccount: null,
            yearSpaceParams: {
                factorA: 0,
                FORIncrease: 0,
                FORDecrease: 0,
                grossYearlyIncome: 0
            },
            yearSpacePensionAccount: {
                typeOfAdditionalPensionAccount: 'saving',
                riskProfileId: riskProfiles.neutral
            },
            otherIncomeAvailable: true,
            otherIncome: {
                gift: undefined,
                heritage: undefined,
                surplusHouse: undefined,
                other: undefined
            }
        },
        general: { aowBase: 0, taxPercentage: 0 },
    });

    return (
        <PensionContext.Provider
            value={{
                parameters: pensionParameters,
                actions: {
                    setParameters: (payload) => setPensionParameters({ type: 'MERGE', payload }),
                    propagate: () => { },
                },
            }}
        >
            {children}
        </PensionContext.Provider>
    );
};
